import { ArrowBack, ArrowForward, DeleteOutline, SwapHoriz } from '@mui/icons-material'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  type ChapterSectionDependency,
  type DependencyKind,
  findCyclePath,
  findDependency,
  wouldViolateOrder,
} from '@shared/chapterSectionDependencies'
import { normalizeForSearch } from '@shared/textNormalize'
import React, { useEffect, useMemo, useState } from 'react'

import { DEPENDENCY_KINDS, DEPENDENCY_KIND_COPY } from './dependencyKindLabels'

export interface DepSectionOption {
  key: string
  label: string
  /** Optional group header (e.g. chapter title) used by the Autocomplete's groupBy. */
  groupTitle?: string
}

interface AddDependencyDialogProps {
  open: boolean
  bookDirection: 'ltr' | 'rtl'
  bookFontFamily?: string
  /** All candidate sections for both Autocomplete pickers. */
  sections: DepSectionOption[]
  /** Existing deps used for cycle detection (and to populate existing reasons). */
  existingDeps: ChapterSectionDependency[]
  /** Linear order used for order-conflict detection. */
  orderedKeys: string[]
  /** Pre-fill from-endpoint when opening. */
  initialFromKey?: string
  /** Pre-fill to-endpoint when opening. */
  initialToKey?: string
  /** Pre-fill the reason textbox (e.g. selected text from the editor). */
  initialReason?: string
  /** Pre-select the kind when opening. Defaults to a seeding link. */
  initialKind?: DependencyKind
  /** Persist the dependency. Called only with non-empty distinct fromKey/toKey. */
  onSave: (fromKey: string, toKey: string, reason: string, kind: DependencyKind) => void | Promise<void>
  /** Delete the existing dep at the current (from, to). */
  onDelete?: (depId: string) => void | Promise<void>
  onCancel: () => void
}

export default function AddDependencyDialog({
  open,
  bookDirection,
  bookFontFamily,
  sections,
  existingDeps,
  orderedKeys,
  initialFromKey = '',
  initialToKey = '',
  initialReason = '',
  initialKind = 'seed',
  onSave,
  onDelete,
  onCancel,
}: AddDependencyDialogProps) {
  const [fromKey, setFromKey] = useState(initialFromKey)
  const [toKey, setToKey] = useState(initialToKey)
  const [reason, setReason] = useState(initialReason)
  const [kind, setKind] = useState<DependencyKind>(initialKind)

  useEffect(() => {
    if (open) {
      setFromKey(initialFromKey)
      setToKey(initialToKey)
      setReason(initialReason)
      setKind(initialKind)
    }
  }, [open, initialFromKey, initialToKey, initialReason, initialKind])

  const sectionByKey = useMemo(() => {
    const m = new Map<string, DepSectionOption>()
    for (const s of sections) {
      m.set(s.key, s)
    }
    return m
  }, [sections])

  const fromOption = fromKey ? (sectionByKey.get(fromKey) ?? null) : null
  const toOption = toKey ? (sectionByKey.get(toKey) ?? null) : null

  const existingDep = fromKey && toKey ? findDependency(existingDeps, fromKey, toKey) : undefined
  const isExisting = existingDep !== undefined
  const copy = DEPENDENCY_KIND_COPY[kind]
  const isValidPair = Boolean(fromKey && toKey && fromKey !== toKey)

  const cyclePathKeys = useMemo(() => {
    if (!isValidPair) {
      return null
    }
    return findCyclePath(existingDeps, fromKey, toKey)
  }, [existingDeps, fromKey, toKey, isValidPair])

  const cyclePathLabels = useMemo(() => {
    if (!cyclePathKeys) {
      return null
    }
    return cyclePathKeys.map((k) => sectionByKey.get(k)?.label ?? k)
  }, [cyclePathKeys, sectionByKey])

  const orderConflict = useMemo(() => {
    if (!isValidPair) {
      return false
    }
    return wouldViolateOrder(fromKey, toKey, orderedKeys)
  }, [fromKey, toKey, orderedKeys, isValidPair])

  const cycleConflict = cyclePathLabels !== null

  const Arrow = bookDirection === 'rtl' ? ArrowBack : ArrowForward
  const titleText = isExisting ? 'עריכת תלות' : 'תלות חדשה'

  const handleSwap = () => {
    setFromKey(toKey)
    setToKey(fromKey)
  }

  const handleDelete = () => {
    if (existingDep && onDelete) {
      void onDelete(existingDep.id)
    }
  }

  const handleSave = () => {
    if (!isValidPair) {
      return
    }
    void onSave(fromKey, toKey, reason, kind)
  }

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth dir={bookDirection}>
      <DialogTitle>{titleText}</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ mb: 2 }}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={kind}
            onChange={(_, next) => {
              if (next) {
                setKind(next as DependencyKind)
              }
            }}
          >
            {DEPENDENCY_KINDS.map((k) => (
              <ToggleButton key={k} value={k} sx={{ textTransform: 'none', px: 1.5 }}>
                {DEPENDENCY_KIND_COPY[k].name}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Typography
            sx={{
              fontSize: '0.75rem',
              color: 'text.secondary',
              mt: 0.75,
              textAlign: bookDirection === 'rtl' ? 'right' : 'left',
            }}
          >
            {copy.hint}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
            mb: 2,
            direction: bookDirection,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: '0.7rem',
                color: 'text.secondary',
                mb: 0.5,
                textAlign: bookDirection === 'rtl' ? 'right' : 'left',
              }}
            >
              {copy.fromLabel}
            </Typography>
            <SectionAutocomplete
              value={fromOption}
              onChange={(opt) => setFromKey(opt?.key ?? '')}
              sections={sections}
              excludeKey={toKey}
              bookDirection={bookDirection}
              bookFontFamily={bookFontFamily}
              placeholder="בחר מקטע..."
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0.5,
              flexShrink: 0,
              pt: 3,
            }}
          >
            <Arrow sx={{ color: 'text.secondary' }} />
            <Tooltip title="החלף כיוון" placement="top">
              <span>
                <IconButton size="small" onClick={handleSwap} disabled={!fromKey && !toKey} sx={{ p: 0.25 }}>
                  <SwapHoriz sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: '0.7rem',
                color: 'text.secondary',
                mb: 0.5,
                textAlign: bookDirection === 'rtl' ? 'right' : 'left',
              }}
            >
              {copy.toLabel}
            </Typography>
            <SectionAutocomplete
              value={toOption}
              onChange={(opt) => setToKey(opt?.key ?? '')}
              sections={sections}
              excludeKey={fromKey}
              bookDirection={bookDirection}
              bookFontFamily={bookFontFamily}
              placeholder="בחר מקטע..."
            />
          </Box>
        </Box>
        <TextField
          label="הסבר (אופציונלי)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          fullWidth
          multiline
          minRows={2}
          maxRows={6}
          autoFocus
          inputProps={{ dir: bookDirection }}
        />
        {isExisting && (
          <Typography sx={{ fontSize: '0.75rem', color: 'info.main', mt: 1 }}>
            כבר קיימת תלות בין שני המקטעים הללו. שמירה תעדכן את הסוג ואת ההסבר.
          </Typography>
        )}
        {(cycleConflict || orderConflict) && (
          <Stack spacing={1} sx={{ mt: 1.5 }}>
            {cycleConflict && cyclePathLabels && (
              <Alert severity="error" variant="outlined" sx={{ py: 1 }}>
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, mb: 0.75 }}>
                  שמירת התלות תיצור לולאה:
                </Typography>
                <CyclePathDisplay
                  labels={cyclePathLabels}
                  bookDirection={bookDirection}
                  bookFontFamily={bookFontFamily}
                />
                <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.75 }}>
                  כל חץ הוא תלות — המקטע שלפניו חייב להגיע לפני זה שאחריו. החץ המקווקו הוא התלות החדשה שעומדת להיווצר.
                </Typography>
              </Alert>
            )}
            {orderConflict && !cycleConflict && (
              <Alert severity="warning" variant="outlined" sx={{ py: 0.5 }}>
                בסדר הנוכחי המקטעים ממוקמים הפוך — שמירת התלות תוסיף הפרה. נדרש ריאורדור כדי לקיים אותה.
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Box>
          {isExisting && onDelete && (
            <Button onClick={handleDelete} color="error" startIcon={<DeleteOutline />}>
              מחק תלות
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onCancel} color="inherit">
            ביטול
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={!isValidPair}>
            שמירה
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  )
}

interface SectionAutocompleteProps {
  value: DepSectionOption | null
  onChange: (opt: DepSectionOption | null) => void
  sections: DepSectionOption[]
  /** A key to hide from the option list (typically the other endpoint, to prevent self-loops). */
  excludeKey?: string
  bookDirection: 'ltr' | 'rtl'
  bookFontFamily?: string
  placeholder?: string
}

function SectionAutocomplete({
  value,
  onChange,
  sections,
  excludeKey,
  bookDirection,
  bookFontFamily,
  placeholder,
}: SectionAutocompleteProps) {
  const filtered = useMemo(() => {
    if (!excludeKey) {
      return sections
    }
    return sections.filter((s) => s.key !== excludeKey)
  }, [sections, excludeKey])
  const hasGroups = useMemo(() => filtered.some((s) => s.groupTitle !== undefined), [filtered])
  return (
    <Autocomplete
      size="small"
      options={filtered}
      value={value}
      onChange={(_, next) => onChange(next)}
      getOptionLabel={(opt) => opt.label}
      isOptionEqualToValue={(a, b) => a.key === b.key}
      groupBy={hasGroups ? (opt) => opt.groupTitle ?? '' : undefined}
      filterOptions={(opts, state) => {
        const q = normalizeForSearch(state.inputValue)
        if (!q) {
          return opts
        }
        return opts.filter((o) => normalizeForSearch(o.label).includes(q))
      }}
      renderInput={(params) => (
        <TextField {...params} placeholder={placeholder} inputProps={{ ...params.inputProps, dir: bookDirection }} />
      )}
      slotProps={{
        paper: {
          sx: { dir: bookDirection },
        },
      }}
      componentsProps={{
        popper: {
          placement: 'bottom-start',
        },
      }}
      renderOption={(props, option) => (
        <li
          {...props}
          key={option.key}
          style={{
            fontFamily: bookFontFamily || 'SBL Hebrew',
            direction: bookDirection,
            textAlign: bookDirection === 'rtl' ? 'right' : 'left',
          }}
        >
          {option.label}
        </li>
      )}
      renderGroup={(params) => (
        <li key={params.key}>
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 1,
              px: 1.25,
              py: 0.5,
              fontSize: '0.7rem',
              fontWeight: 700,
              color: 'text.secondary',
              bgcolor: 'background.paper',
              borderBottom: '1px solid',
              borderColor: 'divider',
              direction: bookDirection,
              textAlign: bookDirection === 'rtl' ? 'right' : 'left',
              fontFamily: bookFontFamily || 'SBL Hebrew',
            }}
          >
            {params.group || 'ללא פרק'}
          </Box>
          <ul style={{ padding: 0, margin: 0 }}>{params.children}</ul>
        </li>
      )}
    />
  )
}

interface CyclePathDisplayProps {
  labels: string[]
  bookDirection: 'ltr' | 'rtl'
  bookFontFamily?: string
}

/**
 * Compact chips separated by arrows representing the would-be cycle.
 * First arrow (between indices 0 and 1) is dashed/red — the proposed new edge.
 */
function CyclePathDisplay({ labels, bookDirection, bookFontFamily }: CyclePathDisplayProps) {
  if (labels.length < 2) {
    return null
  }
  const ArrowChar = bookDirection === 'rtl' ? '←' : '→'
  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 0.5,
        direction: bookDirection,
      }}
    >
      {labels.map((label, idx) => (
        <React.Fragment key={`${idx}-${label}`}>
          <Box
            sx={{
              px: 1,
              py: 0.25,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'error.main',
              bgcolor: 'background.paper',
              fontFamily: bookFontFamily || 'SBL Hebrew',
              fontSize: '0.8rem',
              maxWidth: 180,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={label}
          >
            {label}
          </Box>
          {idx < labels.length - 1 && (
            <Box
              component="span"
              sx={{
                color: idx === 0 ? 'error.main' : 'text.secondary',
                fontWeight: idx === 0 ? 700 : 400,
                fontSize: '1rem',
                px: 0.25,
                textDecoration: idx === 0 ? 'underline dashed' : 'none',
                textUnderlineOffset: 3,
              }}
              title={idx === 0 ? 'התלות החדשה' : 'תלות קיימת'}
            >
              {ArrowChar}
            </Box>
          )}
        </React.Fragment>
      ))}
    </Box>
  )
}
